/* eslint-disable react/jsx-props-no-spreading */
import React, { HTMLAttributes } from 'react';
import { ReactComponent as MinusIcon } from 'assets/icons_3/minus.svg';
import { ReactComponent as PlusIcon } from 'assets/icons_3/plus.svg';
import classNames from 'classnames';
import Badge from 'components/UI_kit_3/Badge/Badge';
import { useTranslation } from 'react-i18next';
import FormHint from 'components/UI_kit_3/FormHint/FormHint';
import { IMaskInput } from 'react-imask';
import styles from './RangeInput.module.scss';

interface IRangeInputProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'value'> {
  value: number | null;
  label?: string;
  min: number;
  max: number;
  error?: string;
  name?: string;
  disabled?: boolean;
  step?: number;
  hint?: string;
  onChange: (value: number) => void;
  qaElement?: string;
}

const RangeInput: React.FC<IRangeInputProps> = ({
  onChange, value, label, name, min, max, hint,
  disabled, error, step = 1, qaElement, className, ...props
}) => {
  const [t] = useTranslation();
  const handleSelectRange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange(Number(e.target.value));
  };

  const handleIncrease = () => {
    onChange((value || 0) + step);
  };

  const handleDecrease = () => {
    onChange((value || 0) - step);
  };

  const snapToStep = (num: number) => {
    const steps = Math.round((num - min) / step);
    return Math.min(max, Math.max(min, min + steps * step));
  };

  return (
    <div className={classNames(styles.rangeInput, className)}>
      <div className={styles.label}>
        <label htmlFor={props.id}>{label}</label>
        <div className={styles.btnGroup}>
          <button type="button" aria-label="minus" disabled={(value || 0) <= min || disabled} onClick={handleDecrease} qa-element={`${qaElement}-decrease`}><MinusIcon /></button>
          <IMaskInput
            mask={Number}
            min={min}
            max={max}
            size={String(max).length}
            scale={0}
            signed={false}
            value={value !== null ? String(value) : ''}
            disabled={disabled}
            qa-element={`${qaElement}-value`}
            className={styles.manualInput}
            onAccept={(val: string, _mask: unknown, e?: InputEvent) => {
              if (!e && val !== String(value)) return;
              const num = Number(val);
              if (val !== '' && !Number.isNaN(num) && num >= min && num <= max) {
                onChange(num);
              }
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              const rawValue = e.target.value;
              const num = Number(rawValue);
              if (rawValue === '' || Number.isNaN(num)) {
                onChange(min);
              } else {
                onChange(snapToStep(num));
              }
            }}
          />
          <button type="button" aria-label="plus" disabled={(value || 0) >= max || disabled} onClick={handleIncrease} qa-element={`${qaElement}-increase`}><PlusIcon /></button>
        </div>
        {hint && <FormHint className={styles.hint}>{hint}</FormHint>}
      </div>
      <input
        value={value || min}
        type="range"
        name={name}
        disabled={disabled}
        min={Number(min)}
        max={Number(max)}
        step={step}
        onChange={handleSelectRange}
        qa-element={qaElement}
        {...props}
      />
      <div className={styles.rangeBadges}>
        <div>
          <Badge size="lg" className={styles.badge} variant="gray" pill>{min}</Badge>
          <div>{t('common.min_long')}</div>
        </div>
        <div>
          <Badge size="lg" className={styles.badge} variant="gray" pill>{max}</Badge>
          <div>{t('common.max_long')}</div>
        </div>
      </div>
    </div>
  );
};

export default RangeInput;
